import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, ClipboardCheck, Plus, Edit, X, Loader2, CheckCircle2, AlertCircle, Camera, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import api from '../../services/api';

const Sv2ChecklistManagement = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [entities, setEntities] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [formData, setFormData] = useState({
        itemName: '',
        description: '',
        groupName: 'General',
        isRequired: true,
        isPhotoRequired: false,
        isConditional: false,
        conditionNote: '',
        isActive: true,
        selectedEntityIds: []
    });
    const [submitting, setSubmitting] = useState(false);

    // Collapsible groups state
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());

    const pageSize = 10;
    const totalPages = Math.ceil(totalCount / pageSize);

    useEffect(() => {
        fetchEntities();
        fetchItems();
    }, [currentPage]);

    const fetchEntities = async () => {
        try {
            const response = await api.getEntities();
            setEntities(response.items || []);
        } catch (error) {
            console.error('Error fetching entities:', error);
        }
    };

    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await api.getSv2ChecklistItems(currentPage, pageSize);
            setItems(response.items || []);
            setTotalCount(response.totalCount);
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to fetch SV2 checklist items',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                itemName: formData.itemName,
                description: formData.description,
                groupName: formData.groupName,
                isRequired: formData.isRequired,
                isPhotoRequired: formData.isPhotoRequired,
                isConditional: formData.isConditional,
                conditionNote: formData.isConditional ? formData.conditionNote : null,
                isActive: formData.isActive,
                applicableEntityIds: formData.selectedEntityIds.length > 0 ? formData.selectedEntityIds : null
            };
            await api.createSv2ChecklistItem(payload);
            toast({
                title: 'Success',
                description: 'SV2 checklist item added successfully'
            });
            setShowAddModal(false);
            setFormData({ itemName: '', description: '', groupName: 'General', isRequired: true, isPhotoRequired: false, isConditional: false, conditionNote: '', isActive: true, selectedEntityIds: [] });
            fetchItems();
        } catch (error) {
            console.error('Error adding SV2 checklist item:', error);
            const errorMessage = error.response?.data?.message
                || error.response?.data?.title
                || error.message
                || 'Failed to add item';
            toast({
                title: 'Error',
                description: errorMessage,
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                itemNumber: selectedItem.itemNumber,
                itemName: formData.itemName,
                description: formData.description,
                groupName: formData.groupName,
                groupDisplayOrder: selectedItem.groupDisplayOrder,
                isRequired: formData.isRequired,
                isPhotoRequired: formData.isPhotoRequired,
                isConditional: formData.isConditional,
                conditionNote: formData.isConditional ? formData.conditionNote : null,
                isActive: formData.isActive,
                applicableEntityIds: formData.selectedEntityIds.length > 0 ? formData.selectedEntityIds : null
            };
            await api.updateSv2ChecklistItem(selectedItem.id, payload);
            toast({
                title: 'Success',
                description: 'SV2 checklist item updated successfully'
            });
            setShowEditModal(false);
            setSelectedItem(null);
            fetchItems();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to update item',
                variant: 'destructive'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;

        try {
            await api.deleteSv2ChecklistItem(id);
            toast({
                title: 'Success',
                description: 'SV2 checklist item deleted successfully'
            });
            fetchItems();
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to delete item',
                variant: 'destructive'
            });
        }
    };

    const openAddModal = () => {
        // Reset form data to default empty values
        setFormData({
            itemName: '',
            description: '',
            groupName: 'General',
            isRequired: true,
            isPhotoRequired: false,
            isConditional: false,
            conditionNote: '',
            isActive: true,
            selectedEntityIds: []
        });
        setSelectedItem(null);
        setShowAddModal(true);
    };

    const openEditModal = (item) => {
        setSelectedItem(item);
        setFormData({
            itemName: item.itemName,
            description: item.description || '',
            groupName: item.groupName || 'General',
            isRequired: item.isRequired,
            isPhotoRequired: item.isPhotoRequired || false,
            isConditional: item.isConditional,
            conditionNote: item.conditionNote || '',
            isActive: item.isActive,
            selectedEntityIds: item.applicableEntityIds || []
        });
        setShowEditModal(true);
    };

    const toggleEntitySelection = (entityId) => {
        setFormData(prev => ({
            ...prev,
            selectedEntityIds: prev.selectedEntityIds.includes(entityId)
                ? prev.selectedEntityIds.filter(id => id !== entityId)
                : [...prev.selectedEntityIds, entityId]
        }));
    };

    const getEntityName = (entityId) => {
        const entity = entities.find(e => e.id === entityId);
        return entity ? entity.entityName : 'Unknown';
    };

    const toggleGroupCollapse = (groupName) => {
        setCollapsedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupName)) {
                newSet.delete(groupName);
            } else {
                newSet.add(groupName);
            }
            return newSet;
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <Toaster />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/admin/settings')}
                        className="mb-4"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Settings
                    </Button>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shrink-0">
                                <ClipboardCheck className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">SV2 Checklist Master</h1>
                                <p className="text-gray-600 text-sm sm:text-base">Total {totalCount} checklist items</p>
                            </div>
                        </div>

                        <Button className="bg-amber-600 hover:bg-amber-700 text-white self-start sm:self-auto" onClick={openAddModal}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add Checklist Item
                        </Button>
                    </div>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>All SV2 Checklist Items</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center items-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="text-center py-12 bg-gray-50 rounded-lg">
                                <AlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                <p className="text-gray-600 mb-4">No SV2 checklist items yet</p>
                                <Button
                                    onClick={openAddModal}
                                    variant="outline"
                                    className="border-amber-600 text-amber-600 hover:bg-amber-50"
                                >
                                    <Plus className="h-4 w-4 mr-2" /> Add First Item
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-6">
                                    {(() => {
                                        // Group items by group name
                                        const groupedItems = items.reduce((acc, item) => {
                                            const groupKey = item.groupName || 'General';
                                            if (!acc[groupKey]) {
                                                acc[groupKey] = {
                                                    groupName: groupKey,
                                                    groupDisplayOrder: item.groupDisplayOrder ?? 999,
                                                    items: []
                                                };
                                            }
                                            acc[groupKey].items.push(item);
                                            return acc;
                                        }, {});

                                        // Sort groups by groupDisplayOrder, then items within groups
                                        const sortedGroups = Object.values(groupedItems)
                                            .sort((a, b) => a.groupDisplayOrder - b.groupDisplayOrder);

                                        return sortedGroups.map((group, groupIndex) => {
                                            const sortedItems = group.items.sort((a, b) =>
                                                a.itemNumber - b.itemNumber || a.id.localeCompare(b.id)
                                            );
                                            const isCollapsed = collapsedGroups.has(group.groupName);

                                            return (
                                                <div key={groupIndex} className="space-y-3">
                                                    {/* Group Header */}
                                                    <div
                                                        className="flex items-center gap-3 mb-3 cursor-pointer hover:opacity-75 transition-opacity"
                                                        onClick={() => toggleGroupCollapse(group.groupName)}
                                                    >
                                                        <div className="h-px bg-gray-300 flex-1"></div>
                                                        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
                                                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                                                {group.groupName}
                                                            </h4>
                                                            <span className="text-xs text-gray-500">({sortedItems.length})</span>
                                                            {isCollapsed ? (
                                                                <ChevronDown className="h-4 w-4 text-gray-600" />
                                                            ) : (
                                                                <ChevronUp className="h-4 w-4 text-gray-600" />
                                                            )}
                                                        </div>
                                                        <div className="h-px bg-gray-300 flex-1"></div>
                                                    </div>

                                                    {/* Group Items */}
                                                    {!isCollapsed && sortedItems.map((item, itemIndex) => (
                                                        <div
                                                            key={item.id}
                                                            className="p-4 bg-white border border-gray-200 rounded-lg hover:border-amber-300 transition-all"
                                                        >
                                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                                                                <div className="flex items-start sm:items-center gap-2 flex-1 min-w-0">
                                                                    <span className="w-8 h-8 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">
                                                                        {item.itemNumber}
                                                                    </span>
                                                                    <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2">
                                                                        <h4 className="font-semibold text-gray-900 break-words sm:truncate">{item.itemName}</h4>
                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                            {item.isRequired && (
                                                                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded shrink-0">
                                                                                    Required
                                                                                </span>
                                                                            )}
                                                                            {item.isConditional && (
                                                                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1 shrink-0">
                                                                                    <AlertCircle className="h-3 w-3" /> Conditional
                                                                                </span>
                                                                            )}
                                                                            {item.isPhotoRequired && (
                                                                                <span className="text-xs text-blue-600 flex items-center gap-1 shrink-0">
                                                                                    <Camera className="h-3 w-3" /> Photo
                                                                                </span>
                                                                            )}
                                                                            {!item.applicableEntityIds || item.applicableEntityIds.length === 0 ? (
                                                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded shrink-0">
                                                                                    Universal (All Entities)
                                                                                </span>
                                                                            ) : (
                                                                                <div className="flex flex-wrap gap-1">
                                                                                    {item.applicableEntityIds.map(entityId => (
                                                                                        <span key={entityId} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                                                                            {getEntityName(entityId)}
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                            {!item.isActive && (
                                                                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded shrink-0">
                                                                                    Inactive
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex gap-2 shrink-0 self-end sm:self-auto">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        onClick={() => openEditModal(item)}
                                                                        className="h-8 w-8"
                                                                    >
                                                                        <Edit className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        onClick={() => handleDelete(item.id)}
                                                                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                    >
                                                                        <X className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                            {item.description && (
                                                                <p className="text-sm text-gray-600 ml-10 mt-2">{item.description}</p>
                                                            )}
                                                            {item.isConditional && item.conditionNote && (
                                                                <div className="ml-10 mt-1">
                                                                    <span className="text-xs font-medium text-amber-700">Condition: </span>
                                                                    <span className="text-xs text-gray-600">{item.conditionNote}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>

                                {totalPages > 1 && (
                                    <div className="flex items-center justify-between border-t pt-4 mt-4">
                                        <div className="text-sm text-gray-500">
                                            Page {currentPage} of {totalPages} ({totalCount} total)
                                        </div>
                                        <div className="flex gap-2">
                                            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                                                Previous
                                            </Button>
                                            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                                                Next
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
                    <Card className="w-full max-w-md mx-auto my-8 max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Add SV2 Checklist Item</CardTitle>
                                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleAdd} className="space-y-4">
                                <div>
                                    <Label htmlFor="itemName">Item Name *</Label>
                                    <Input id="itemName" value={formData.itemName} onChange={(e) => setFormData({ ...formData, itemName: e.target.value })} required placeholder="Verify installation quality" />
                                </div>
                                <div>
                                    <Label htmlFor="groupName">Group Name *</Label>
                                    <Input id="groupName" value={formData.groupName} onChange={(e) => setFormData({ ...formData, groupName: e.target.value })} required placeholder="General" />
                                </div>
                                <div>
                                    <Label htmlFor="description">Description</Label>
                                    <Textarea id="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} placeholder="Check all details" />
                                </div>
                                <div>
                                    <Label className="block mb-2">Applicable To</Label>
                                    <div className="p-3 border rounded-md bg-gray-50 space-y-2 max-h-48 overflow-y-auto">
                                        <p className="text-xs text-gray-600 mb-2">
                                            Leave all unchecked to apply to ALL entities (universal). Select specific entities to limit applicability.
                                        </p>
                                        {entities.map(entity => (
                                            <div key={entity.id} className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    id={`entity-${entity.id}`}
                                                    checked={formData.selectedEntityIds.includes(entity.id)}
                                                    onChange={() => toggleEntitySelection(entity.id)}
                                                    className="rounded border-gray-300"
                                                />
                                                <Label htmlFor={`entity-${entity.id}`} className="cursor-pointer">
                                                    {entity.entityName}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="isRequired"
                                            checked={formData.isRequired}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                isRequired: e.target.checked,
                                                isConditional: e.target.checked ? false : formData.isConditional
                                            })}
                                            className="rounded border-gray-300"
                                        />
                                        <Label htmlFor="isRequired">Required</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="isPhotoRequired"
                                            checked={formData.isPhotoRequired}
                                            onChange={(e) => setFormData({ ...formData, isPhotoRequired: e.target.checked })}
                                            className="rounded border-gray-300"
                                        />
                                        <Label htmlFor="isPhotoRequired">Photo Required</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="isConditional"
                                            checked={formData.isConditional}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                isConditional: e.target.checked,
                                                isRequired: e.target.checked ? false : formData.isRequired,
                                                conditionNote: e.target.checked ? formData.conditionNote : ''
                                            })}
                                            className="rounded border-gray-300"
                                        />
                                        <Label htmlFor="isConditional">Conditional</Label>
                                    </div>
                                    <p className="text-xs text-gray-500">Note: An item cannot be both Required and Conditional</p>
                                </div>
                                {formData.isConditional && (
                                    <div>
                                        <Label htmlFor="conditionNote">Condition Note *</Label>
                                        <Textarea id="conditionNote" value={formData.conditionNote} onChange={(e) => setFormData({ ...formData, conditionNote: e.target.value })} rows={3} required={formData.isConditional} placeholder="Describe when this item is applicable" />
                                    </div>
                                )}
                                <div className="flex justify-end space-x-3 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} disabled={submitting}>Cancel</Button>
                                    <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={submitting}>
                                        {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Adding...</> : 'Add Item'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}

            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
                    <Card className="w-full max-w-md mx-auto my-8 max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Edit SV2 Checklist Item</CardTitle>
                                <Button variant="ghost" size="sm" onClick={() => setShowEditModal(false)}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleEdit} className="space-y-4">
                                <div>
                                    <Label htmlFor="editItemName">Item Name *</Label>
                                    <Input id="editItemName" value={formData.itemName} onChange={(e) => setFormData({ ...formData, itemName: e.target.value })} required />
                                </div>
                                <div>
                                    <Label htmlFor="editGroupName">Group Name *</Label>
                                    <Input id="editGroupName" value={formData.groupName} onChange={(e) => setFormData({ ...formData, groupName: e.target.value })} required />
                                </div>
                                <div>
                                    <Label htmlFor="editDescription">Description</Label>
                                    <Textarea id="editDescription" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
                                </div>
                                <div>
                                    <Label className="block mb-2">Applicable To</Label>
                                    <div className="p-3 border rounded-md bg-gray-50 space-y-2 max-h-48 overflow-y-auto">
                                        <p className="text-xs text-gray-600 mb-2">
                                            Leave all unchecked to apply to ALL entities (universal). Select specific entities to limit applicability.
                                        </p>
                                        {entities.map(entity => (
                                            <div key={entity.id} className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    id={`edit-entity-${entity.id}`}
                                                    checked={formData.selectedEntityIds.includes(entity.id)}
                                                    onChange={() => toggleEntitySelection(entity.id)}
                                                    className="rounded border-gray-300"
                                                />
                                                <Label htmlFor={`edit-entity-${entity.id}`} className="cursor-pointer">
                                                    {entity.entityName}
                                                </Label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="editIsRequired"
                                            checked={formData.isRequired}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                isRequired: e.target.checked,
                                                isConditional: e.target.checked ? false : formData.isConditional
                                            })}
                                            className="rounded border-gray-300"
                                        />
                                        <Label htmlFor="editIsRequired">Required</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="editIsPhotoRequired"
                                            checked={formData.isPhotoRequired}
                                            onChange={(e) => setFormData({ ...formData, isPhotoRequired: e.target.checked })}
                                            className="rounded border-gray-300"
                                        />
                                        <Label htmlFor="editIsPhotoRequired">Photo Required</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            id="editIsConditional"
                                            checked={formData.isConditional}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                isConditional: e.target.checked,
                                                isRequired: e.target.checked ? false : formData.isRequired,
                                                conditionNote: e.target.checked ? formData.conditionNote : ''
                                            })}
                                            className="rounded border-gray-300"
                                        />
                                        <Label htmlFor="editIsConditional">Conditional</Label>
                                    </div>
                                    <p className="text-xs text-gray-500">Note: An item cannot be both Required and Conditional</p>
                                </div>
                                {formData.isConditional && (
                                    <div>
                                        <Label htmlFor="editConditionNote">Condition Note *</Label>
                                        <Textarea id="editConditionNote" value={formData.conditionNote} onChange={(e) => setFormData({ ...formData, conditionNote: e.target.value })} rows={3} required={formData.isConditional} />
                                    </div>
                                )}
                                <div className="flex items-center space-x-2">
                                    <input type="checkbox" id="editIsActive" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} className="rounded border-gray-300" />
                                    <Label htmlFor="editIsActive">Active</Label>
                                </div>
                                <div className="flex justify-end space-x-3 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} disabled={submitting}>Cancel</Button>
                                    <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={submitting}>
                                        {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating...</> : 'Update Item'}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default Sv2ChecklistManagement;
